import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BedIcon from '@mui/icons-material/Bed';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DoorFrontIcon from '@mui/icons-material/DoorFront';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';
import { formatDateDisplay, formatDateInput, formatDateTimeDisplay, todayDateInput } from '../../utils/dateUtils';

const COPY = {
  ar: {
    title: 'تقرير التسكين',
    subtitle: 'استعراض كامل لحركة إيواء الخيل عبر كل الإسطبلات — نشِطة ومُخلاة، مع إمكانية الاستخراج.',
    search: 'بحث باسم الخيل، الرقاقة، أو المالك',
    stable: 'الإسطبل',
    allStables: 'كل الإسطبلات',
    type: 'نوع الإيواء',
    allTypes: 'كل الأنواع',
    status: 'الحالة',
    allStatuses: 'كل الحالات',
    active: 'نشِطة (لم يُسجَّل خروجها)',
    transferred: 'نُقلت لغرفة أخرى (لم تُخلَ الإسطبل)',
    checkedOut: 'مُخلاة (خروج فعليّ)',
    from: 'من تاريخ',
    to: 'إلى تاريخ',
    refresh: 'تحديث',
    reset: 'إعادة ضبط',
    exportExcel: 'تصدير Excel',
    total: 'إجمالي السجلات',
    activeCount: 'إقامات نشِطة',
    transferredCount: 'نُقلت بين الغرف',
    checkedOutCount: 'إقامات مُخلاة فعليًّا',
    horses: 'خيول مختلفة',
    stables: 'إسطبلات مختلفة',
    noData: 'لا توجد سجلات إيواء ضمن الفلاتر الحالية.',
    loadError: 'تعذّر تحميل تقرير التسكين.',
    noExportData: 'لا توجد بيانات للتصدير.',
    table: {
      horse: 'الخيل',
      location: 'الإسطبل/الغرفة',
      type: 'النوع',
      startDate: 'تاريخ التسكين',
      endDate: 'تاريخ الإخلاء',
      days: 'عدد الأيام',
      status: 'الحالة',
      owner: 'المالك',
      createdBy: 'سجَّله',
      checkedOutBy: 'أخلاه',
    },
  },
  en: {
    title: 'Accommodation Report',
    subtitle: 'Full view of horse accommodation activity across all stables — active and checked-out, exportable.',
    search: 'Search by horse name, RFID, or owner',
    stable: 'Stable',
    allStables: 'All stables',
    type: 'Accommodation type',
    allTypes: 'All types',
    status: 'Status',
    allStatuses: 'All statuses',
    active: 'Active (not checked out)',
    transferred: 'Moved to another room (still on-site)',
    checkedOut: 'Checked out (actually left)',
    from: 'From',
    to: 'To',
    refresh: 'Refresh',
    reset: 'Reset',
    exportExcel: 'Export Excel',
    total: 'Total records',
    activeCount: 'Active stays',
    transferredCount: 'Moved between rooms',
    checkedOutCount: 'Actually checked out',
    horses: 'Distinct horses',
    stables: 'Distinct stables',
    noData: 'No accommodation records match the current filters.',
    loadError: 'Unable to load the accommodation report.',
    noExportData: 'No data to export.',
    table: {
      horse: 'Horse',
      location: 'Stable/Room',
      type: 'Type',
      startDate: 'Check-in',
      endDate: 'Check-out',
      days: 'Days',
      status: 'Status',
      owner: 'Owner',
      createdBy: 'Checked in by',
      checkedOutBy: 'Checked out by',
    },
  },
};

const daysAgoInput = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return formatDateInput(date);
};

// -- ExcelJS adapter (نفس نمط OwnerList.js) --
const xlCol = (n) => {
  let s = '';
  let num = n;
  while (num > 0) { const rem = (num - 1) % 26; s = String.fromCharCode(65 + rem) + s; num = Math.floor((num - 1) / 26); }
  return s;
};
const xlRef = (r, c) => `${xlCol(c + 1)}${r + 1}`;
const xlArgb = (rgb) => ({ argb: `FF${rgb}` });
const xlStyle = (s) => {
  if (!s) return {};
  const out = {};
  if (s.font) out.font = { bold: s.font.bold, sz: s.font.sz, color: s.font.color?.rgb ? xlArgb(s.font.color.rgb) : undefined };
  if (s.fill?.fgColor?.rgb) out.fill = { type: 'pattern', pattern: 'solid', fgColor: xlArgb(s.fill.fgColor.rgb) };
  if (s.alignment) out.alignment = { ...s.alignment, vertical: s.alignment.vertical === 'center' ? 'middle' : s.alignment.vertical };
  return out;
};
const xlMake = (wb, name, aoa) => { const ws = wb.addWorksheet(name); aoa.forEach((row) => ws.addRow(row)); return ws; };

export default function AccommodationReportPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const text = COPY[lang];
  const isRtl = lang === 'ar';

  const [filters, setFilters] = useState({
    search: '',
    stable_id: 'all',
    type_id: 'all',
    status: 'all',
    from: daysAgoInput(30),
    to: todayDateInput(),
  });
  const [data, setData] = useState({ items: [], summary: {}, filters: { stables: [], types: [] } });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const updateFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ from: filters.from, to: filters.to });
      if (filters.search.trim()) params.set('search', filters.search.trim());
      if (filters.stable_id !== 'all') params.set('stable_id', filters.stable_id);
      if (filters.type_id !== 'all') params.set('type_id', filters.type_id);
      if (filters.status !== 'all') params.set('status', filters.status);

      const response = await api.get(`/accommodation-report?${params.toString()}`);
      setData(response.data || { items: [], summary: {}, filters: { stables: [], types: [] } });
    } catch (err) {
      setError(err.response?.data?.details || err.response?.data?.error || text.loadError);
    } finally {
      setLoading(false);
    }
  }, [filters, text.loadError]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFilters = () => {
    setFilters({ search: '', stable_id: 'all', type_id: 'all', status: 'all', from: daysAgoInput(30), to: todayDateInput() });
  };

  const statCards = useMemo(() => {
    const summary = data.summary || {};
    return [
      { key: 'total', label: text.total, value: summary.total || 0, icon: <BedIcon />, tone: 'gold' },
      { key: 'activeCount', label: text.activeCount, value: summary.active || 0, icon: <DoorFrontIcon />, tone: 'green' },
      { key: 'transferredCount', label: text.transferredCount, value: summary.transferred || 0, icon: <FilterAltIcon />, tone: 'warning' },
      { key: 'checkedOutCount', label: text.checkedOutCount, value: summary.checkedOut || 0, icon: <LogoutIcon />, tone: 'rose' },
      { key: 'horses', label: text.horses, value: summary.distinctHorses || 0, icon: <FilterAltIcon />, tone: 'blue' },
      { key: 'stables', label: text.stables, value: summary.distinctStables || 0, icon: <HomeWorkIcon />, tone: 'gold' },
    ];
  }, [data.summary, text]);

  const toneColor = {
    gold: { color: COLORS.gold, soft: COLORS.goldSoft },
    blue: { color: COLORS.blue, soft: COLORS.blueSoft },
    green: { color: COLORS.green, soft: COLORS.greenSoft },
    rose: { color: COLORS.rose, soft: COLORS.roseSoft },
    warning: { color: COLORS.warning, soft: COLORS.warningSoft },
  };

  const horseLabel = (row) => (lang === 'ar' ? row.horse_name_arabic || row.horse_name : row.horse_name || row.horse_name_arabic) || '-';

  const statusMeta = (status) => {
    if (status === 'active') return { label: text.active, color: COLORS.green, soft: COLORS.greenSoft };
    if (status === 'transferred') return { label: text.transferred, color: COLORS.warning, soft: COLORS.warningSoft };
    return { label: text.checkedOut, color: COLORS.rose, soft: COLORS.roseSoft };
  };

  const handleExport = async () => {
    const rows = data.items || [];
    if (!rows.length) {
      setError(text.noExportData);
      return;
    }
    const columns = [
      { header: '#', key: 'idx', width: 6 },
      { header: text.table.horse, key: 'horse', width: 26 },
      { header: 'RFID', key: 'rfid', width: 18 },
      { header: text.table.location, key: 'location', width: 28 },
      { header: text.table.type, key: 'type', width: 20 },
      { header: text.table.startDate, key: 'start', width: 14 },
      { header: text.table.endDate, key: 'end', width: 14 },
      { header: text.table.days, key: 'days', width: 10 },
      { header: text.table.status, key: 'status', width: 14 },
      { header: text.table.owner, key: 'owner', width: 26 },
      { header: text.table.createdBy, key: 'createdBy', width: 16 },
      { header: text.table.checkedOutBy, key: 'checkedOutBy', width: 16 },
    ];
    const exportRows = rows.map((row, index) => ({
      idx: index + 1,
      horse: horseLabel(row),
      rfid: row.rfid_chip_number || '-',
      location: row.stable_name && row.room_name ? `${row.stable_name} - ${row.room_name}` : '-',
      type: (lang === 'ar' ? row.type_name_ar || row.type_name_en : row.type_name_en || row.type_name_ar) || '-',
      start: row.start_date ? formatDateDisplay(row.start_date) : '-',
      end: row.end_date ? formatDateDisplay(row.end_date) : '-',
      days: row.days_count ?? '-',
      status: statusMeta(row.status).label,
      owner: [row.owner_first_name, row.owner_last_name].filter(Boolean).join(' ') || '-',
      createdBy: row.created_by_username || '-',
      checkedOutBy: row.checked_out_by_username || '-',
    }));

    const wsData = [];
    wsData.push([`${text.title} — ${exportRows.length}`]);
    wsData.push(columns.map((c) => c.header));
    exportRows.forEach((row) => wsData.push(columns.map((c) => row[c.key])));

    const wb = new ExcelJS.Workbook();
    const ws = xlMake(wb, 'Accommodation', wsData);
    ws.mergeCells(`A1:${xlRef(0, columns.length - 1)}`);
    columns.forEach((c, i) => { ws.getColumn(i + 1).width = c.width; });
    ws.pageSetup = { paperSize: 9, orientation: 'landscape', fitToWidth: 1, fitToHeight: 0 };
    ws.getCell('A1').style = xlStyle({ alignment: { horizontal: 'center', vertical: 'center' }, font: { bold: true, sz: 14 }, fill: { fgColor: { rgb: 'E2C67F' } } });
    for (let c = 0; c < columns.length; c += 1) {
      ws.getCell(xlRef(1, c)).style = xlStyle({ fill: { fgColor: { rgb: 'F9E7B6' } }, font: { bold: true }, alignment: { horizontal: 'center', vertical: 'center' } });
    }

    const buffer = await wb.xlsx.writeBuffer();
    const stamp = new Date().toISOString().slice(0, 10);
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `Accommodation_Report_${stamp}.xlsx`);
  };

  return (
    <Box
      className="smart-page-container"
      sx={{ minHeight: '100vh', bgcolor: COLORS.page, width: '100%', maxWidth: 'var(--app-page-max)', mx: 'auto', overflowX: 'hidden', direction: isRtl ? 'rtl' : 'ltr' }}
    >
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.title}</Typography>
            <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>{text.subtitle}</Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button onClick={handleExport} variant="outlined" startIcon={<FileDownloadIcon />} disabled={!data.items?.length} sx={{ borderColor: COLORS.line, color: COLORS.text, fontWeight: 800, '&:hover': { borderColor: COLORS.gold, bgcolor: COLORS.cardSoft } }}>
              {text.exportExcel}
            </Button>
            <Button onClick={load} variant="contained" startIcon={<RefreshIcon />} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>
              {text.refresh}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(6, minmax(0, 1fr))' }, gap: 2, mb: 3 }}>
        {statCards.map((card) => {
          const palette = toneColor[card.tone];
          return (
            <Paper key={card.key} elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 104 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: palette.soft, color: palette.color, display: 'grid', placeItems: 'center' }}>{card.icon}</Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: palette.color, fontWeight: 900, fontSize: 12.5 }}>{card.label}</Typography>
                  <Typography sx={{ color: COLORS.text, fontWeight: 900, fontSize: 24, mt: 0.3 }}>{Number(card.value).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-US')}</Typography>
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Box>

      <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }}>
          <TextField
            value={filters.search}
            onChange={(event) => updateFilter('search', event.target.value)}
            placeholder={text.search}
            size="small"
            sx={{ flex: 1, minWidth: { lg: 280 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          <TextField select label={text.stable} value={filters.stable_id} onChange={(event) => updateFilter('stable_id', event.target.value)} size="small" sx={{ width: { xs: '100%', lg: 200 } }}>
            <MenuItem value="all">{text.allStables}</MenuItem>
            {(data.filters?.stables || []).map((stable) => (
              <MenuItem key={stable.stable_id} value={stable.stable_id}>{stable.stable_name}</MenuItem>
            ))}
          </TextField>
          <TextField select label={text.type} value={filters.type_id} onChange={(event) => updateFilter('type_id', event.target.value)} size="small" sx={{ width: { xs: '100%', lg: 190 } }}>
            <MenuItem value="all">{text.allTypes}</MenuItem>
            {(data.filters?.types || []).map((type) => (
              <MenuItem key={type.type_id} value={type.type_id}>{lang === 'ar' ? type.type_name_ar || type.type_name_en : type.type_name_en || type.type_name_ar}</MenuItem>
            ))}
          </TextField>
          <TextField select label={text.status} value={filters.status} onChange={(event) => updateFilter('status', event.target.value)} size="small" sx={{ width: { xs: '100%', lg: 190 } }}>
            <MenuItem value="all">{text.allStatuses}</MenuItem>
            <MenuItem value="active">{text.active}</MenuItem>
            <MenuItem value="transferred">{text.transferred}</MenuItem>
            <MenuItem value="checked_out">{text.checkedOut}</MenuItem>
          </TextField>
          <TextField
            type="date"
            label={text.from}
            value={filters.from}
            onChange={(event) => updateFilter('from', event.target.value)}
            size="small"
            sx={{ width: { xs: '100%', lg: 170 } }}
            InputLabelProps={{ shrink: true }}
            InputProps={{ startAdornment: <CalendarMonthIcon sx={{ color: COLORS.muted, mx: 0.5 }} /> }}
          />
          <TextField type="date" label={text.to} value={filters.to} onChange={(event) => updateFilter('to', event.target.value)} size="small" sx={{ width: { xs: '100%', lg: 170 } }} InputLabelProps={{ shrink: true }} />
          <Button onClick={load} variant="contained" sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.refresh}</Button>
          <Button onClick={resetFilters} variant="outlined" startIcon={<FilterAltIcon />}>{text.reset}</Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontWeight: 900, color: COLORS.text }}>{text.title}</Typography>
          <Chip label={`${Number(data.items?.length || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-US')} ${text.total}`} sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 900 }} />
        </Stack>
        <Divider />
        <TableContainer sx={{ maxHeight: 'calc(100vh - 480px)' }}>
          <Table stickyHeader size="small" sx={{ minWidth: 1280 }}>
            <TableHead>
              <TableRow>
                {Object.values(text.table).map((label) => (
                  <TableCell key={label} sx={{ bgcolor: '#f4efe4', color: COLORS.text, fontWeight: 900, borderBottom: `2px solid ${COLORS.gold}`, whiteSpace: 'nowrap' }}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8 }}><CircularProgress size={28} /></TableCell>
                </TableRow>
              ) : data.items?.length ? (
                data.items.map((row) => (
                  <TableRow key={row.accommodation_id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800, fontSize: 13 }}>{horseLabel(row)}</Typography>
                      <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>{row.rfid_chip_number || '-'}</Typography>
                    </TableCell>
                    <TableCell>{row.stable_name && row.room_name ? `${row.stable_name} - ${row.room_name}` : '-'}</TableCell>
                    <TableCell>{(lang === 'ar' ? row.type_name_ar || row.type_name_en : row.type_name_en || row.type_name_ar) || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><span className="date-ltr">{row.start_date ? formatDateDisplay(row.start_date) : '-'}</span></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.end_date ? <span className="date-ltr">{formatDateDisplay(row.end_date)}</span> : '-'}
                    </TableCell>
                    <TableCell align="center">{row.days_count ?? '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusMeta(row.status).label}
                        sx={{ bgcolor: statusMeta(row.status).soft, color: statusMeta(row.status).color, fontWeight: 800 }}
                      />
                    </TableCell>
                    <TableCell>{[row.owner_first_name, row.owner_last_name].filter(Boolean).join(' ') || '-'}</TableCell>
                    <TableCell>{row.created_by_username || '-'}</TableCell>
                    <TableCell>
                      {row.checked_out_by_username ? (
                        <Tooltip title={row.checked_out_at ? formatDateTimeDisplay(row.checked_out_at) : ''}>
                          <span>{row.checked_out_by_username}</span>
                        </Tooltip>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8, color: COLORS.muted }}>{text.noData}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
